import styled from 'styled-components'
import supportsWebP from 'supports-webp'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useLocationQueryParams } from 'utils/hooks'

import * as ticketingSystemsSelector from 'selectors/ticketingSystems'
import * as sessionUserSelector from 'selectors/sessionUser'

import MainBackground4KWebp from 'assets/img/MainBG3840x2160.webp'
import MainBackground4KJpg from 'assets/img/MainBG3840x2160.jpg'
import MainBackground2KWebp from 'assets/img/MainBG2560x1440.webp'
import MainBackground2KJpg from 'assets/img/MainBG2560x1440.jpg'
import MainBackgroundFHDWebp from 'assets/img/MainBG1920x1080.webp'
import MainBackgroundFHDJpg from 'assets/img/MainBG1920x1080.jpg'

import { RouteTicketingSystemsByPartner } from 'routes'
import colors from 'components/utils/colors'
import { capitalizeFirstLetter } from 'utils/strings'

import Notification from 'components/Shared/Notification/Index'
import Footer from 'components/App/Footer/Index'
import ScrollableTabs from 'components/Shared/ScrollableTabs/Index'
import TicketingSystemsList from './TicketingSystemsList'

const StyledMain = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`

const NotificationWrapper = styled.div`
  position: relative;
  top: 1px;
`

const ScrollablePartnerTabsPanel = styled.div`
  position: relative;
  top: 1px;
  right: 0;
  left: 0;
  background: ${colors.secondary98};
  border-bottom: 1px solid ${colors.secondary70};
  padding: 0 8px;
`

const HeaderPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 140px;
  padding-top: 45px;
  box-sizing: border-box;
  background-color: #FFF;
`

const GreetingLine = styled.h1`
  font-size: 37px;
  line-height: 51px;
  margin-bottom: 2px;
`
const Greeting = styled.span`
  font-weight: 300;
  color: ${colors.secondary20};
`
const UserName = styled.span`
  color: #171717;
  margin-left: 14px;
`
const Subheader = styled.div`
  font-size: 14px;
  color: ${colors.secondary20};
  text-align: center;
  line-height: 22px;
  font-weight: 400;
`

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;
  flex: 1;
  justify-content: center;
  align-items: center;
  @media (max-width: 1920px) {
    background-image: url(${supportsWebP ? MainBackgroundFHDWebp : MainBackgroundFHDJpg});
  }
  @media (min-width: 1921px) and (max-width: 2560px) {
    background-image: url(${supportsWebP ? MainBackground2KWebp : MainBackground2KJpg});
  }
  @media (min-width: 2561px) {
    background-image: url(${supportsWebP ? MainBackground4KWebp : MainBackground4KJpg});
  }
  background-color: #F0F0F0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-top: 1px solid ${colors.secondary70};
`

const WelcomePage = () => {
  const userDisplayName = useSelector(sessionUserSelector.displayName)
  let ticketingSystems = useSelector(ticketingSystemsSelector.ticketingSystems)
  const isFetching = useSelector(ticketingSystemsSelector.isFetching)
  const partnerTicketingSystems = useSelector(sessionUserSelector.partnerTicketingSystems)
  const isUserPrivileged = useSelector(sessionUserSelector.isUserPrivileged)

  const { t } = useTranslation()
  const [queryParams] = useLocationQueryParams()
  const partnerId = queryParams.partnerId

  if (isFetching) {
    return null
  }

  const partnerTabs = []
  let activeTabIndex = 0
  if (isUserPrivileged) {
    if (partnerId) {
      ticketingSystems = ticketingSystems.filter(ts => ts.partnerId === partnerId)
    }
    partnerTicketingSystems.forEach((partner, idx) => {
      partnerTabs.push({
        title: capitalizeFirstLetter(partner.name),
        to: RouteTicketingSystemsByPartner({ partnerId: partner.id }),
      })
      if (partnerId === partner.id) {
        activeTabIndex = idx
      }
    })
  }

  return (
    <StyledMain>
      {(isUserPrivileged) && (
        <ScrollablePartnerTabsPanel>
          <ScrollableTabs items={partnerTabs} activeTabIndex={activeTabIndex} />
        </ScrollablePartnerTabsPanel>
      )}

      <NotificationWrapper>
        <Notification />
      </NotificationWrapper>

      <HeaderPanel>
        <GreetingLine>
          <Greeting>{t('welcomePage.greeting')}</Greeting>
          <UserName>{userDisplayName}</UserName>
        </GreetingLine>
        <Subheader>{t('welcomePage.selectTS')}</Subheader>
      </HeaderPanel>

      <StyledContent>
        <TicketingSystemsList ticketingSystems={ticketingSystems} />
      </StyledContent>

      <Footer />
    </StyledMain>
  )
}

export default WelcomePage
